import { FocusEvent, useMemo } from 'react';
import { css } from '@emotion/react';
import { debounce } from 'lodash-es';
import { InvoiceDetailModel } from '@frontend/api-invoices';
import { Trans, useTranslation } from '@frontend/i18n';
import { CardBrand } from '@frontend/payments-card-brand';
import { Text, useFormField, ListField, PostalCodeField, CheckboxField } from '@frontend/design-system';

interface ReceiptSectionProps {
  emailFieldProps: ReturnType<typeof useFormField<'list'>>;
  saveCardCheckboxFieldProps: ReturnType<typeof useFormField<'checkbox'>>;
  saveCardZipFieldProps: ReturnType<typeof useFormField<'text'>>;
  personEmail: string | undefined;
  autoReceiptEnabled: boolean;
  invoice: InvoiceDetailModel | undefined;
}

const block = css`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-md);
  margin-top: var(--fabric-space-gap-md);
  margin-left: auto;
  margin-right: auto;
`;

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-md);
    width: 100%;
  `,
  block,
  saveCardBlock: [
    block,
    css`
      border: solid 1px var(--fabric-color-neutral-20);
      border-radius: var(--fabric-radius-md);
      padding: var(--fabric-space-padding-md);
    `,
  ],
  cardInfoBlock: css`
    display: flex;
    gap: var(--fabric-space-gap-xs);
    align-items: center;
  `,
  cardBrandBlock: css`
    flex-shrink: 0;

    & > div {
      flex-wrap: nowrap;
    }
  `,
};

export const ReceiptSection = ({
  emailFieldProps: { onBlur: onBlurEmailField, ...emailFieldProps },
  saveCardZipFieldProps: { onBlur: onBlurSaveCardZipField, ...saveCardZipFieldProps },
  saveCardCheckboxFieldProps: { onBlur: onBlurSaveCardCheckboxField, ...saveCardCheckboxFieldProps },
  invoice,
  personEmail,
  autoReceiptEnabled,
}: ReceiptSectionProps) => {
  const { t } = useTranslation('payments');

  const fieldTitle =
    personEmail && autoReceiptEnabled ? (
      <Trans t={t}>
        Receipt emailed to <b>{personEmail}</b>. You can send another receipt to an additional email address here
      </Trans>
    ) : (
      t('Send a receipt to this email')
    );

  const fieldLabel = personEmail && autoReceiptEnabled ? t('Additional Email') : t('Email');

  /**
   * For some reason, the blur even conclicts with the "click" event
   * on the modal footer primary action.
   * The debounce fixes it.
   */
  const onBlurEmailDebounced = useMemo(() => {
    return debounce((e?: FocusEvent<HTMLInputElement, Element>) => {
      if (typeof onBlurEmailField === 'function') {
        onBlurEmailField(e);
      }
    }, 150);
  }, []);

  const onBlurSaveCardZipDebounced = useMemo(() => {
    return debounce((e?: FocusEvent<HTMLInputElement, Element>) => {
      if (typeof onBlurSaveCardZipField === 'function') {
        onBlurSaveCardZipField(e);
      }
    }, 150);
  }, []);

  const onBlurSaveCardCheckboxDebounced = useMemo(() => {
    return debounce((e?: FocusEvent<HTMLInputElement, Element>) => {
      if (typeof onBlurSaveCardCheckboxField === 'function') {
        onBlurSaveCardCheckboxField(e);
      }
    }, 150);
  }, []);

  const paymentDetails = invoice?.payment?.paymentDetails;

  return (
    <div className='receipt-section'>
      <div className='receipt-section__email-block' css={styles.block}>
        <Text size='large'>{fieldTitle}:</Text>
        <ListField
          {...emailFieldProps}
          onBlur={onBlurEmailDebounced}
          name='email'
          label={fieldLabel}
          fieldType='email'
        />
      </div>
      <div className='receipt-section__save-card-block' css={styles.saveCardBlock}>
        <div className='card-block__info' css={styles.cardInfoBlock}>
          <div>
            <CheckboxField
              {...saveCardCheckboxFieldProps}
              onBlur={onBlurSaveCardCheckboxDebounced}
              name='saveCard'
              label=''
            />
          </div>
          <div>
            <Text weight='bold'>{t('Save card for future payments')}</Text>
            <Text>{t('Securely store this card on the patient’s profile.')}</Text>
          </div>
          <div css={styles.cardBrandBlock}>
            <CardBrand brand={paymentDetails?.brand} last4={paymentDetails?.lastFour} />
          </div>
        </div>

        {saveCardCheckboxFieldProps.value && (
          <div className='card-block__input'>
            <PostalCodeField
              {...saveCardZipFieldProps}
              onBlur={onBlurSaveCardZipDebounced}
              disabled={!saveCardCheckboxFieldProps.value}
              name='zip'
              label={t('Billing ZIP code')}
              placeholder={t('Billing ZIP code')}
            />
          </div>
        )}
      </div>
    </div>
  );
};
